import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Users, TrendingUp, Clock, Target, Zap, Award, Activity, BarChart2,
  CheckCircle2, XCircle, AlertTriangle, ArrowRight, RefreshCw, Filter, MapPin
} from 'lucide-react';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer, RadarChart, PolarGrid,
  PolarAngleAxis, PolarRadiusAxis, Radar, Cell
} from 'recharts';

const LaborPerformance = () => {
  const [timeRange, setTimeRange] = useState('today'); // today, week, month
  const [selectedWorker, setSelectedWorker] = useState(null);

  // Mock data for worker performance
  const workers = [
    {
      id: 1,
      name: 'John Doe',
      role: 'Picker',
      department: 'Outbound',
      metrics: {
        pickRate: 145, // items per hour
        accuracy: 99.2, // percentage
        tasksCompleted: 23,
        avgTimePerTask: 4.2, // minutes
        interleavedTasks: 8,
        efficiency: 92.5
      },
      todayTasks: [
        { type: 'pick', order: 'ORD-2024-156', items: 12, time: 5.2, status: 'completed' },
        { type: 'putaway', po: 'PO-2024-089', items: 25, time: 8.5, status: 'completed' },
        { type: 'pick', order: 'ORD-2024-157', items: 8, time: 3.8, status: 'in-progress' }
      ],
      weeklyTrend: [
        { day: 'Mon', pickRate: 140, accuracy: 98.5 },
        { day: 'Tue', pickRate: 145, accuracy: 99.0 },
        { day: 'Wed', pickRate: 142, accuracy: 99.2 },
        { day: 'Thu', pickRate: 148, accuracy: 99.5 },
        { day: 'Fri', pickRate: 145, accuracy: 99.2 }
      ]
    },
    {
      id: 2,
      name: 'Jane Smith',
      role: 'Picker',
      department: 'Outbound',
      metrics: {
        pickRate: 132,
        accuracy: 98.8,
        tasksCompleted: 20,
        avgTimePerTask: 4.8,
        interleavedTasks: 6,
        efficiency: 88.3
      },
      todayTasks: [
        { type: 'pick', order: 'ORD-2024-158', items: 15, time: 6.1, status: 'completed' },
        { type: 'pick', order: 'ORD-2024-159', items: 10, time: 4.5, status: 'completed' }
      ],
      weeklyTrend: [
        { day: 'Mon', pickRate: 130, accuracy: 98.0 },
        { day: 'Tue', pickRate: 135, accuracy: 98.5 },
        { day: 'Wed', pickRate: 132, accuracy: 98.8 },
        { day: 'Thu', pickRate: 130, accuracy: 98.6 },
        { day: 'Fri', pickRate: 132, accuracy: 98.8 }
      ]
    },
    {
      id: 3,
      name: 'Mike Wilson',
      role: 'Receiver',
      department: 'Inbound',
      metrics: {
        pickRate: 0,
        accuracy: 97.5,
        tasksCompleted: 15,
        avgTimePerTask: 6.5,
        interleavedTasks: 4,
        efficiency: 85.0
      },
      todayTasks: [
        { type: 'receive', po: 'PO-2024-092', items: 120, time: 12.3, status: 'completed' },
        { type: 'putaway', po: 'PO-2024-092', items: 120, time: 15.2, status: 'in-progress' }
      ],
      weeklyTrend: [
        { day: 'Mon', pickRate: 0, accuracy: 97.0 },
        { day: 'Tue', pickRate: 0, accuracy: 97.5 },
        { day: 'Wed', pickRate: 0, accuracy: 97.3 },
        { day: 'Thu', pickRate: 0, accuracy: 97.8 },
        { day: 'Fri', pickRate: 0, accuracy: 97.5 }
      ]
    },
    {
      id: 4,
      name: 'Sarah Johnson',
      role: 'QC Inspector',
      department: 'Quality Control',
      metrics: {
        pickRate: 0,
        accuracy: 99.8,
        tasksCompleted: 18,
        avgTimePerTask: 3.5,
        interleavedTasks: 0,
        efficiency: 95.2
      },
      todayTasks: [
        { type: 'qc', po: 'PO-2024-089', items: 50, time: 8.2, status: 'completed' },
        { type: 'qc', po: 'PO-2024-092', items: 25, time: 4.1, status: 'completed' }
      ],
      weeklyTrend: [
        { day: 'Mon', pickRate: 0, accuracy: 99.5 },
        { day: 'Tue', pickRate: 0, accuracy: 99.7 },
        { day: 'Wed', pickRate: 0, accuracy: 99.8 },
        { day: 'Thu', pickRate: 0, accuracy: 99.9 },
        { day: 'Fri', pickRate: 0, accuracy: 99.8 }
      ]
    }
  ];

  // Mock data for task interleaving
  const interleavedTasks = [
    {
      id: 1,
      worker: 'John Doe',
      sequence: [
        { type: 'putaway', po: 'PO-2024-089', location: 'Zone A → Aisle A1 → Rack A1-R1', time: '10:00 AM', status: 'completed' },
        { type: 'pick', order: 'ORD-2024-156', location: 'Zone A → Aisle A1 → Rack A1-R2', time: '10:05 AM', status: 'completed', saved: '15m' },
        { type: 'putaway', po: 'PO-2024-089', location: 'Zone A → Aisle A2 → Rack A2-R1', time: '10:20 AM', status: 'completed' },
        { type: 'pick', order: 'ORD-2024-157', location: 'Zone A → Aisle A2 → Rack A2-R1', time: '10:22 AM', status: 'in-progress', saved: '18m' }
      ],
      totalSaved: '33 minutes',
      efficiencyGain: '28%'
    }
  ];

  // Overall performance metrics
  const overallMetrics = {
    totalWorkers: 45,
    activeWorkers: 38,
    avgPickRate: 138,
    avgAccuracy: 98.7,
    totalTasksCompleted: 1250,
    avgEfficiency: 89.5,
    interleavedTasksToday: 156
  };

  const getPerformanceColor = (value, type) => {
    if (type === 'pickRate') {
      if (value >= 140) return 'text-green-600';
      if (value >= 120) return 'text-yellow-600';
      return 'text-red-600';
    }
    if (type === 'accuracy') {
      if (value >= 99) return 'text-green-600';
      if (value >= 97) return 'text-yellow-600';
      return 'text-red-600';
    }
    if (type === 'efficiency') {
      if (value >= 90) return 'text-green-600';
      if (value >= 80) return 'text-yellow-600';
      return 'text-red-600';
    }
    return 'text-gray-600';
  };

  const renderWorkerCards = () => {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {workers.map((worker) => (
          <motion.div
            key={worker.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => setSelectedWorker(worker.id)}
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="font-semibold text-gray-900">{worker.name}</h3>
                <p className="text-sm text-gray-600">{worker.role} • {worker.department}</p>
              </div>
              <div className={`p-2 rounded-lg ${
                worker.metrics.efficiency >= 90 ? 'bg-green-100' :
                worker.metrics.efficiency >= 80 ? 'bg-yellow-100' :
                'bg-red-100'
              }`}>
                <Award className={`w-5 h-5 ${
                  worker.metrics.efficiency >= 90 ? 'text-green-600' :
                  worker.metrics.efficiency >= 80 ? 'text-yellow-600' :
                  'text-red-600'
                }`} />
              </div>
            </div>
            <div className="space-y-3">
              {worker.role === 'Picker' && (
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-gray-600">Pick Rate</span>
                    <span className={`text-sm font-bold ${getPerformanceColor(worker.metrics.pickRate, 'pickRate')}`}>
                      {worker.metrics.pickRate}/hr
                    </span>
                  </div>
                  <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className={`h-full ${
                        worker.metrics.pickRate >= 140 ? 'bg-green-500' :
                        worker.metrics.pickRate >= 120 ? 'bg-yellow-500' :
                        'bg-red-500'
                      }`}
                      style={{ width: `${(worker.metrics.pickRate / 150) * 100}%` }}
                    />
                  </div>
                </div>
              )}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-gray-600">Accuracy</span>
                  <span className={`text-sm font-bold ${getPerformanceColor(worker.metrics.accuracy, 'accuracy')}`}>
                    {worker.metrics.accuracy}%
                  </span>
                </div>
                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className={`h-full ${
                      worker.metrics.accuracy >= 99 ? 'bg-green-500' :
                      worker.metrics.accuracy >= 97 ? 'bg-yellow-500' :
                      'bg-red-500'
                    }`}
                    style={{ width: `${worker.metrics.accuracy}%` }}
                  />
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-gray-600">Efficiency</span>
                  <span className={`text-sm font-bold ${getPerformanceColor(worker.metrics.efficiency, 'efficiency')}`}>
                    {worker.metrics.efficiency}%
                  </span>
                </div>
                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className={`h-full ${
                      worker.metrics.efficiency >= 90 ? 'bg-green-500' :
                      worker.metrics.efficiency >= 80 ? 'bg-yellow-500' :
                      'bg-red-500'
                    }`}
                    style={{ width: `${worker.metrics.efficiency}%` }}
                  />
                </div>
              </div>
              <div className="pt-3 border-t border-gray-200">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-600">Tasks Today</span>
                  <span className="font-semibold text-gray-900">{worker.metrics.tasksCompleted}</span>
                </div>
                {worker.metrics.interleavedTasks > 0 && (
                  <div className="flex items-center justify-between text-xs mt-1">
                    <span className="text-gray-600">Interleaved</span>
                    <span className="font-semibold text-green-600">{worker.metrics.interleavedTasks}</span>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    );
  };

  const renderWorkerDetail = () => {
    if (!selectedWorker) return null;
    const worker = workers.find(w => w.id === selectedWorker);
    if (!worker) return null;

    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
      >
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-xl font-bold text-gray-900">{worker.name}</h3>
            <p className="text-sm text-gray-600">{worker.role} • {worker.department}</p>
          </div>
          <button
            onClick={() => setSelectedWorker(null)}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Close
          </button>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div>
            <h4 className="font-semibold text-gray-900 mb-4">Performance Metrics</h4>
            <div className="space-y-4">
              {[
                { label: 'Pick Rate', value: `${worker.metrics.pickRate} items/hr`, color: getPerformanceColor(worker.metrics.pickRate, 'pickRate') },
                { label: 'Accuracy', value: `${worker.metrics.accuracy}%`, color: getPerformanceColor(worker.metrics.accuracy, 'accuracy') },
                { label: 'Tasks Completed', value: worker.metrics.tasksCompleted, color: 'text-gray-900' },
                { label: 'Avg Time/Task', value: `${worker.metrics.avgTimePerTask} min`, color: 'text-gray-900' },
                { label: 'Interleaved Tasks', value: worker.metrics.interleavedTasks, color: 'text-green-600' },
                { label: 'Efficiency', value: `${worker.metrics.efficiency}%`, color: getPerformanceColor(worker.metrics.efficiency, 'efficiency') }
              ].map((metric, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <span className="text-sm text-gray-600">{metric.label}</span>
                  <span className={`text-sm font-semibold ${metric.color}`}>{metric.value}</span>
                </div>
              ))}
            </div>
          </div>
          <div>
            <h4 className="font-semibold text-gray-900 mb-4">Weekly Trend</h4>
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={worker.weeklyTrend}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="day" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="pickRate" stroke="#3b82f6" name="Pick Rate" />
                <Line type="monotone" dataKey="accuracy" stroke="#10b981" name="Accuracy %" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="mt-6">
          <h4 className="font-semibold text-gray-900 mb-4">Today's Tasks</h4>
          <div className="space-y-2">
            {worker.todayTasks.map((task, index) => (
              <div key={index} className="p-3 border border-gray-200 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-sm font-medium text-gray-900 capitalize">{task.type}</span>
                    {task.order && <span className="text-sm text-gray-600 ml-2">{task.order}</span>}
                    {task.po && <span className="text-sm text-gray-600 ml-2">{task.po}</span>}
                    <span className="text-sm text-gray-600 ml-2">• {task.items} items</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-gray-600">{task.time} min</span>
                    {task.status === 'completed' ? (
                      <CheckCircle2 className="w-5 h-5 text-green-600" />
                    ) : (
                      <Clock className="w-5 h-5 text-orange-600" />
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </motion.div>
    );
  };

  const renderTaskInterleaving = () => {
    return (
      <div className="space-y-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Task Interleaving</h3>
              <p className="text-sm text-gray-600 mt-1">Sequencing tasks to reduce empty travel and increase efficiency</p>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Total Saved Today:</span>
              <span className="text-lg font-bold text-green-600">{overallMetrics.interleavedTasksToday} tasks</span>
            </div>
          </div>
          {interleavedTasks.map((task) => (
            <motion.div
              key={task.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="p-6 border border-gray-200 rounded-lg mb-4"
            >
              <div className="flex items-center justify-between mb-4">
                <h4 className="font-semibold text-gray-900">{task.worker}</h4>
                <div className="text-right">
                  <span className="text-sm text-gray-600">Time Saved:</span>
                  <span className="text-lg font-bold text-green-600 ml-2">{task.totalSaved}</span>
                  <span className="text-sm text-gray-600 ml-2">({task.efficiencyGain} gain)</span>
                </div>
              </div>
              <div className="space-y-3">
                {task.sequence.map((step, index) => (
                  <div key={index} className="relative">
                    <div className={`flex items-start gap-4 p-4 rounded-lg ${
                      step.status === 'completed' ? 'bg-green-50 border-2 border-green-200' :
                      step.status === 'in-progress' ? 'bg-orange-50 border-2 border-orange-200' :
                      'bg-gray-50 border-2 border-gray-200'
                    }`}>
                      <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                        step.status === 'completed' ? 'bg-green-500 text-white' :
                        step.status === 'in-progress' ? 'bg-orange-500 text-white' :
                        'bg-gray-300 text-gray-700'
                      }`}>
                        {index + 1}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <span className="font-semibold text-gray-900 capitalize">{step.type}</span>
                            {step.po && <span className="text-sm text-gray-600 ml-2">{step.po}</span>}
                            {step.order && <span className="text-sm text-gray-600 ml-2">{step.order}</span>}
                          </div>
                          <div className="text-right">
                            <span className="text-sm text-gray-600">{step.time}</span>
                            {step.saved && (
                              <span className="ml-2 text-xs text-green-600 font-medium">Saved {step.saved}</span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-start gap-2">
                          <MapPin className="w-4 h-4 text-gray-500 mt-0.5" />
                          <p className="text-sm text-gray-700">{step.location}</p>
                        </div>
                      </div>
                    </div>
                    {index < task.sequence.length - 1 && (
                      <div className="flex items-center justify-center my-2">
                        <ArrowRight className="w-5 h-5 text-gray-400" />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Overall Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {[
          { label: 'Total Workers', value: overallMetrics.totalWorkers, icon: Users, color: 'blue' },
          { label: 'Active Workers', value: overallMetrics.activeWorkers, icon: Activity, color: 'green' },
          { label: 'Avg Pick Rate', value: `${overallMetrics.avgPickRate}/hr`, icon: Zap, color: 'orange' },
          { label: 'Avg Accuracy', value: `${overallMetrics.avgAccuracy}%`, icon: Target, color: 'purple' }
        ].map((metric, index) => {
          const Icon = metric.icon;
          return (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">{metric.label}</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">{metric.value}</p>
                </div>
                <div className={`p-3 rounded-lg bg-${metric.color}-100`}>
                  <Icon className={`w-6 h-6 text-${metric.color}-600`} />
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Worker Performance Cards */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-gray-900">Individual Performance Metrics</h2>
          <div className="flex gap-2">
            <select
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
            >
              <option value="today">Today</option>
              <option value="week">This Week</option>
              <option value="month">This Month</option>
            </select>
          </div>
        </div>
        {renderWorkerCards()}
      </div>

      {/* Worker Detail */}
      {selectedWorker && renderWorkerDetail()}

      {/* Task Interleaving */}
      {renderTaskInterleaving()}
    </div>
  );
};

export default LaborPerformance;
